import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api.js";
import { Notice, compactCost, compactTokens, formatDuration } from "../components.js";

type Range = "today" | "7d" | "30d" | "all";

const RANGE_LABEL: Record<Range, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

interface DashboardData {
  range: string;
  generatedAt: string;
  live: {
    worker: { running: boolean; active: number; processed: number; succeeded: number; failed: number };
    jobs: { pending: number; processing: number; batched: number; failed: number; paused: number };
    batch: {
      enabled: boolean;
      inFlight: number;
      completed: number;
      failed: number;
      /** Past OpenAI's 24h completion window — stuck by definition, not slow. */
      stuck: number;
    };
    pipeline: { generated: number; inReview: number; approved: number; pendingReview: number };
    /** Coverage and failures only. There is no stored critique verdict, so no
     *  pass rate is reported — see the dashboard route. */
    critique: { total: number; critiqued: number; pending: number; failed: number };
    grounding: {
      total: number;
      retrieved: number;
      zeroResult: number;
      notInTextbook: number;
      ungrounded: number;
    };
    failures: {
      total: number;
      byType: { generate: number; critique: number };
      byReason: { reason: string; count: number }[];
    };
    timing: { generate: number | null; critique: number | null };
  };
  window: {
    generated: number;
    approved: number;
    jobs: number;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    cost: {
      total: number;
      generation: number;
      critique: number;
      testRuns: number;
      /** Null when nothing was generated — "$0.00 per item" would read as free. */
      perItem: number | null;
    };
  };
  system: { environment: string; aiProvider: string; collections: number; batchMode: string };
}

/** A metric with its own destination. Every figure on this page leads
 *  somewhere — a dashboard number nobody can drill into is decoration. */
function Stat({
  value,
  label,
  tone,
  to,
}: {
  value: React.ReactNode;
  label: string;
  tone?: "good" | "bad" | "active";
  to?: string;
}) {
  const colour =
    tone === "good" ? "var(--green)" : tone === "bad" ? "var(--red)" : tone === "active" ? "var(--brand)" : undefined;

  const body = (
    <>
      <span className="queue-stat-value" style={{ color: colour }}>
        {value}
      </span>
      <span className="queue-stat-label">{label}</span>
    </>
  );

  return to ? (
    <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>
      {body}
    </Link>
  ) : (
    <span>{body}</span>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card span-12">
      <div className="row between" style={{ alignItems: "baseline", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted)" }}>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/**
 * Generation operations control centre.
 *
 * Ordered by what an administrator needs first: health, then anything
 * demanding action, then throughput, then quality, then cost, then system
 * detail. Token counts deliberately do not get the same weight as failures.
 *
 * LIVE and RANGE data are kept visually separate. The range selector applies
 * only to the throughput and cost block; worker state, queue depth and
 * failures are always current, because "4 failed" means something different
 * if it silently meant "today".
 */
export function Dashboard() {
  const [range, setRange] = useState<Range>("today");
  const [refreshKey, setRefreshKey] = useState(0);
  const data = useApi<DashboardData>(`/dashboard?range=${range}`, [range, refreshKey]);

  // 10s rather than the queue's 2s: this page is a status overview, not a
  // live job feed, and aggregate queries are heavier.
  useEffect(() => {
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 10_000);
    return () => clearInterval(timer);
  }, []);

  const d = data.data;
  const live = d?.live;
  const win = d?.window;

  const workerDown = live ? !live.worker.running : false;
  const actions: { label: string; detail: string; to: string; cta: string; tone: "bad" | "warn" }[] = [];

  if (live) {
    if (workerDown) {
      actions.push({
        label: "Worker stopped",
        detail: "No jobs will run until it is restarted",
        to: "/settings",
        cta: "Settings",
        tone: "bad",
      });
    }
    if (live.failures.total > 0) {
      actions.push({
        label: `${live.failures.total} failed job${live.failures.total === 1 ? "" : "s"}`,
        detail:
          // Every cause, not just the top two — this is now the only place
          // the breakdown appears.
          live.failures.byReason.map((r) => `${r.reason} (${r.count})`).join(" · ") ||
          "See the queue for detail",
        to: "/queue",
        cta: "Retry",
        tone: "bad",
      });
    }
    if (live.batch.stuck > 0) {
      actions.push({
        label: `${live.batch.stuck} batch job${live.batch.stuck === 1 ? "" : "s"} stuck`,
        detail: "Past the 24-hour completion window",
        to: "/queue",
        cta: "View",
        tone: "bad",
      });
    }
    if (live.pipeline.pendingReview > 0) {
      actions.push({
        label: `${live.pipeline.pendingReview} awaiting review`,
        detail: "Generated content not yet approved",
        to: "/queue?segment=attention",
        cta: "Queue",
        tone: "warn",
      });
    }
    if (live.critique.failed > 0) {
      actions.push({
        label: `${live.critique.failed} critique${live.critique.failed === 1 ? "" : "s"} failed`,
        detail: "Content generated but not reviewed by the critic",
        to: "/queue",
        cta: "View",
        tone: "warn",
      });
    }
  }

  const groundedPct =
    live && live.grounding.total > 0
      ? Math.round((live.grounding.retrieved / live.grounding.total) * 100)
      : null;

  return (
    <>
      {data.error && <Notice kind="error">Cannot load dashboard: {data.error}</Notice>}

      <div className="grid">
        {/* ---------- LEVEL 1: health ---------- */}
        <div className="card span-12">
          <div className="row between" style={{ alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div className="row" style={{ gap: 20, flexWrap: "wrap" }}>
              <span className={`status-dot is-${live?.worker.running ? "active" : "failed"}`}>
                Worker {live?.worker.running ? "running" : "stopped"}
                {live && live.worker.active > 0 ? ` · ${live.worker.active} active` : ""}
              </span>
              <span className={`status-dot is-${live?.batch.enabled ? "active" : "pending"}`}>
                Batch {live?.batch.enabled ? "on" : "off"}
                {live && live.batch.inFlight > 0 ? ` · ${live.batch.inFlight} in flight` : ""}
              </span>
              <span className={`status-dot is-${d?.system.aiProvider === "openai" ? "done" : "pending"}`}>
                {d?.system.aiProvider === "openai" ? "OpenAI" : d?.system.aiProvider ?? "…"}
              </span>
              {groundedPct !== null && (
                <span className={`status-dot is-${groundedPct >= 95 ? "done" : "review"}`}>
                  Retrieval {groundedPct}%
                </span>
              )}
            </div>

            <div className="row" style={{ gap: 8 }}>
              <select value={range} onChange={(e) => setRange(e.target.value as Range)}>
                {(Object.keys(RANGE_LABEL) as Range[]).map((r) => (
                  <option key={r} value={r}>
                    {RANGE_LABEL[r]}
                  </option>
                ))}
              </select>
              <button className="btn" onClick={() => setRefreshKey((k) => k + 1)}>
                Refresh
              </button>
            </div>
          </div>

          {d && (
            <div className="mini muted" style={{ marginTop: 8 }}>
              Updated {new Date(d.generatedAt).toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* ---------- LEVEL 1: action required ---------- */}
        {live && actions.length === 0 && (
          <div className="card span-12">
            <span className="status-dot is-done" style={{ fontSize: 14 }}>
              All systems operational
            </span>
            <div className="mini muted" style={{ marginTop: 4 }}>
              No failed jobs, no stuck batches, nothing waiting for review.
            </div>
          </div>
        )}

        {actions.length > 0 && (
          <Section title="Action required">
            {actions.map((a) => (
              <div
                key={a.label}
                className="row between"
                style={{
                  padding: "10px 0",
                  borderTop: "1px solid var(--line)",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <span className={`status-dot is-${a.tone === "bad" ? "failed" : "review"}`}>
                    {a.label}
                  </span>
                  <div className="mini muted" style={{ marginTop: 2 }}>
                    {a.detail}
                  </div>
                </div>
                <Link className="btn mini" to={a.to}>
                  {a.cta}
                </Link>
              </div>
            ))}
          </Section>
        )}

        {/* ---------- LEVEL 2: live generation ---------- */}
        {live && (
          <Section
            title="Generation · live"
            action={
              <Link className="mini" to="/queue">
                Generation Queue →
              </Link>
            }
          >
            <div className="queue-stats">
              <Stat value={live.jobs.batched} label="in batch" tone="active" to="/queue" />
              <Stat value={live.jobs.processing} label="processing" tone="active" to="/queue" />
              <Stat value={live.jobs.pending} label="queued" to="/queue" />
              {live.jobs.paused > 0 && <Stat value={live.jobs.paused} label="paused" to="/queue" />}
              <Stat
                value={live.jobs.failed}
                label="failed"
                tone={live.jobs.failed > 0 ? "bad" : undefined}
                to="/queue"
              />
            </div>

            {(live.timing.generate || live.timing.critique) && (
              <div className="mini muted" style={{ marginTop: 10 }}>
                Typical generation {formatDuration((live.timing.generate ?? 0) * 1000)}
                {live.timing.critique ? ` · critique ${formatDuration(live.timing.critique * 1000)}` : ""}
              </div>
            )}
          </Section>
        )}

        {/* ---------- LEVEL 3: pipeline ---------- */}
        {live && (
          <Section
            title="Content pipeline"
            action={
              <Link className="mini" to="/queue">
                Generation Queue →
              </Link>
            }
          >
            <div className="row" style={{ gap: 0, flexWrap: "wrap", alignItems: "center" }}>
              {[
                { n: live.grounding.total, l: "Generated", to: "/queue?segment=completed" },
                { n: live.critique.critiqued, l: "Critiqued", to: "/queue?segment=completed" },
                { n: live.pipeline.pendingReview, l: "Awaiting review", to: "/queue?segment=attention" },
                { n: live.pipeline.approved, l: "Approved", to: "/queue?segment=completed" },
              ].map((step, i, all) => (
                <span key={step.l} className="row" style={{ gap: 0, alignItems: "center" }}>
                  <Link
                    to={step.to}
                    style={{ textDecoration: "none", color: "inherit", padding: "0 18px 0 0" }}
                  >
                    <span className="queue-stat-value">{step.n}</span>
                    <span className="queue-stat-label">{step.l}</span>
                  </Link>
                  {i < all.length - 1 && (
                    <span className="muted" style={{ padding: "0 18px 0 0" }} aria-hidden>
                      →
                    </span>
                  )}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* ---------- LEVEL 3: critic + grounding ---------- */}
        {live && (
          <>
            <div className="card span-6">
              <h3
                style={{
                  margin: "0 0 12px",
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--muted)",
                }}
              >
                AI Critic
              </h3>
              <div className="queue-stats">
                <Stat value={live.critique.critiqued} label="critiqued" />
                {live.critique.pending > 0 && <Stat value={live.critique.pending} label="pending" />}
                {live.critique.failed > 0 && (
                  <Stat value={live.critique.failed} label="failed" tone="bad" to="/queue" />
                )}
              </div>
              <div className="mini muted" style={{ marginTop: 10 }}>
                {live.critique.total > 0
                  ? `${live.critique.critiqued} of ${live.critique.total} generations reviewed by the critic.`
                  : "No content critiqued yet."}
              </div>
              {/* Stated rather than silently omitted: an admin should know why
                  there is no pass rate here. */}
              <div className="mini muted" style={{ marginTop: 6 }}>
                Critique feedback is free text, so no pass rate or issue severity is available.
              </div>
            </div>

            <div className="card span-6">
              <h3
                style={{
                  margin: "0 0 12px",
                  fontSize: 13,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "var(--muted)",
                }}
              >
                Source grounding
              </h3>
              {groundedPct === null ? (
                <p className="muted mini" style={{ margin: 0 }}>
                  No generated content yet.
                </p>
              ) : (
                <>
                  <div className="queue-stats">
                    <Stat
                      value={`${groundedPct}%`}
                      label="retrieved source"
                      tone={groundedPct >= 95 ? "good" : "bad"}
                    />
                    {live.grounding.zeroResult > 0 && (
                      <Stat value={live.grounding.zeroResult} label="zero results" tone="bad" />
                    )}
                    {live.grounding.notInTextbook > 0 && (
                      <Stat value={live.grounding.notInTextbook} label="not in textbook" />
                    )}
                  </div>
                  <div className="mini muted" style={{ marginTop: 10 }}>
                    {live.grounding.retrieved} of {live.grounding.total} generations retrieved textbook
                    content.
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ---------- LEVEL 4: cost, scoped to the range ---------- */}
        {win && (
          <Section
            title={`Usage · ${RANGE_LABEL[range]}`}
            action={<span className="mini muted">{win.jobs} jobs</span>}
          >
            <div className="queue-stats">
              <Stat value={compactCost(win.cost.total)} label="total spend" />
              <Stat
                value={win.cost.perItem === null ? "—" : compactCost(win.cost.perItem)}
                label="per item"
              />
              <Stat value={win.generated} label="generated" />
              {win.approved > 0 && <Stat value={win.approved} label="approved" tone="good" />}
            </div>

            <div className="queue-usage" style={{ marginTop: 12 }}>
              <span>{compactTokens(win.inputTokens)} in</span>
              <span>{compactTokens(win.outputTokens)} out</span>
              {win.cachedTokens > 0 && <span>{compactTokens(win.cachedTokens)} cached</span>}
            </div>

            {win.cost.total > 0 && (
              <div style={{ marginTop: 14 }}>
                {[
                  { label: "Generation", value: win.cost.generation },
                  { label: "Critique", value: win.cost.critique },
                  { label: "Test runs", value: win.cost.testRuns },
                  { label: "Pipeline Executions", value: (win.cost as any).pipelines ?? 0 },
                ]
                  .filter((part) => part.value > 0)
                  .map((part) => {
                    const pct = Math.round((part.value / win.cost.total) * 100);
                    return (
                      <div key={part.label} className="row between mini" style={{ padding: "3px 0" }}>
                        <span style={{ minWidth: 110 }}>{part.label}</span>
                        <span className="queue-progress" style={{ flex: 1, margin: "0 12px" }}>
                          <span style={{ width: `${pct}%`, background: "var(--brand)" }} />
                        </span>
                        <span className="muted" style={{ minWidth: 90, textAlign: "right" }}>
                          {compactCost(part.value)} · {pct}%
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}
          </Section>
        )}

        {/* ---------- LEVEL 5: system ---------- */}
        {d && (
          <div className="card span-12">
            <div className="row" style={{ gap: 28, flexWrap: "wrap" }}>
              <span className="mini muted">
                Environment <b style={{ color: "var(--text)" }}>{d.system.environment}</b>
              </span>
              <span className="mini muted">
                Provider <b style={{ color: "var(--text)" }}>{d.system.aiProvider}</b>
              </span>
              <span className="mini muted">
                Batch <b style={{ color: "var(--text)" }}>{d.system.batchMode}</b>
              </span>
              <span className="mini muted">
                Collections <b style={{ color: "var(--text)" }}>{d.system.collections}</b>
              </span>
              <span className="mini muted">
                Worker since start{" "}
                <b style={{ color: "var(--text)" }}>
                  {live?.worker.succeeded ?? 0} ok / {live?.worker.failed ?? 0} failed
                </b>
              </span>
            </div>
          </div>
        )}

        {!d && data.loading && (
          <div className="card span-12">
            <p className="muted">Loading…</p>
          </div>
        )}
      </div>
    </>
  );
}
